import { firestore, storage } from '../database/firebase';
import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  limit,
  startAfter,
  getDoc,
  doc,
  updateDoc,
  deleteDoc,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import toast from 'react-hot-toast';

const DOCUMENTS_COLLECTION = 'documentBank';
const CATEGORIES_COLLECTION = 'documentCategories';
const DOCUMENT_VIEWS_COLLECTION = 'documentBankViews';

function normalizeEmailForStorage(email) {
  if (email == null || email === '') return null;
  const s = String(email).trim().toLowerCase();
  return s || null;
}

/** Persist visibility in a consistent shape (matches Firestore role-based docs). */
export function normalizeDocumentVisibility(v) {
  const m = v && typeof v === 'object' ? v : {};
  const type = typeof m.type === 'string' && m.type.trim() ? m.type.trim() : 'private';
  const rawEmails = Array.isArray(m.userEmails) ? m.userEmails : [];
  const userEmails = rawEmails
    .map(e => (e == null ? '' : String(e).trim().toLowerCase()))
    .filter(Boolean);
  return {
    type,
    roles: Array.isArray(m.roles) ? m.roles : [],
    userIds: Array.isArray(m.userIds) ? m.userIds : [],
    userEmails,
  };
}

export const DEFAULT_CATEGORIES = [
  'Corporate Governance',
  'Financial',
  'Marketing',
  'Operations',
  'Legal',
  'Project Management',
  'Meeting Minutes',
  'Reports',
  'Training Platform - Training',
  'Training Platform - Commercialisation',
  'Training Platform - Commercialisation - Commercialisation Readiness Checklist',
  'Training Platform - Commercialisation - Go to Market Strategy and Commercialisation Plan',
  'Training Platform - Commercialisation - Start-Up Launch Checklist',
  'Training Platform - Scaling',
];

// Lightweight client directory for admin search
export async function listClientsBasic({ search = '', max = 25 } = {}) {
  const lowered = (search || '').toLowerCase();
  try {
    // Firestore doesn't support full-text. Fetch reasonable batch and filter client-side.
    const snap = await getDocs(collection(firestore, 'clients'));
    const items = snap.docs.map(d => {
      const data = d.data() || {};
      return {
        id: d.id,
        email: data.email || '',
        name: `${data.firstname || ''} ${data.lastname || ''}`.trim(),
      };
    });
    const filtered = lowered
      ? items.filter(
          c => c.email.toLowerCase().includes(lowered) || c.name.toLowerCase().includes(lowered)
        )
      : items;
    return filtered.slice(0, Math.max(1, Math.min(max, 50)));
  } catch (_) {
    return [];
  }
}

export async function logDocumentBankView({
  clientId,
  clientEmail = '',
  viewerId,
  viewerEmail = '',
  viewerName = '',
  viewerRole = '',
}) {
  if (!clientId || !viewerId) return;
  try {
    let recentSnap = null;
    try {
      recentSnap = await getDocs(
        query(
          collection(firestore, DOCUMENT_VIEWS_COLLECTION),
          where('clientId', '==', clientId),
          where('viewerId', '==', viewerId),
          orderBy('createdAt', 'desc'),
          limit(1)
        )
      );
    } catch (_) {
      recentSnap = null;
    }

    if (recentSnap && !recentSnap.empty) {
      const latest = recentSnap.docs[0].data();
      const createdAt = latest.createdAt?.toMillis
        ? latest.createdAt.toMillis()
        : latest.createdAt
          ? new Date(latest.createdAt).getTime()
          : 0;
      if (createdAt) {
        const now = Date.now();
        const deltaMinutes = (now - createdAt) / 60000;
        if (deltaMinutes < 30) {
          return;
        }
      }
    }

    await addDoc(collection(firestore, DOCUMENT_VIEWS_COLLECTION), {
      clientId,
      clientEmail,
      viewerId,
      viewerEmail,
      viewerName,
      viewerRole,
      createdAt: serverTimestamp(),
      read: false,
    });
  } catch (_) {
    // fail silently
  }
}

export async function markDocumentBankViewAsRead(id) {
  if (!id) return;
  try {
    await updateDoc(doc(firestore, DOCUMENT_VIEWS_COLLECTION, id), {
      read: true,
    });
  } catch (_) {
    // ignore
  }
}

export async function fetchCategories() {
  try {
    const snap = await getDocs(collection(firestore, CATEGORIES_COLLECTION));
    const dynamic = snap.docs.map(d => d.data()?.name).filter(Boolean);
    const merged = Array.from(new Set([...DEFAULT_CATEGORIES, ...dynamic]));
    return merged;
  } catch (e) {
    return DEFAULT_CATEGORIES;
  }
}

// Initialize categories in Firestore (can be called by admin/coach/business advisor)
export async function initializeCategories() {
  try {
    const existingSnap = await getDocs(collection(firestore, CATEGORIES_COLLECTION));
    const existingCategories = new Set(existingSnap.docs.map(d => d.data()?.name).filter(Boolean));

    const categoriesToAdd = DEFAULT_CATEGORIES.filter(cat => !existingCategories.has(cat));

    if (categoriesToAdd.length === 0) {
      return { success: true, message: 'All categories already exist' };
    }

    const batch = [];
    for (const categoryName of categoriesToAdd) {
      batch.push(
        addDoc(collection(firestore, CATEGORIES_COLLECTION), {
          name: categoryName,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        })
      );
    }

    await Promise.all(batch);
    return { success: true, message: `Added ${categoriesToAdd.length} categories` };
  } catch (error) {
    throw error;
  }
}

// Create a category (for admin/coach/business advisor)
export async function createCategory(categoryName) {
  if (!categoryName || !categoryName.trim()) {
    throw new Error('Category name is required');
  }

  try {
    // Check if category already exists
    const existingSnap = await getDocs(
      query(collection(firestore, CATEGORIES_COLLECTION), where('name', '==', categoryName.trim()))
    );

    if (!existingSnap.empty) {
      throw new Error('Category already exists');
    }

    const docRef = await addDoc(collection(firestore, CATEGORIES_COLLECTION), {
      name: categoryName.trim(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    return { id: docRef.id, name: categoryName.trim() };
  } catch (error) {
    throw error;
  }
}

// Update a category (for admin/coach/business advisor)
export async function updateCategory(categoryId, newName) {
  if (!categoryId || !newName || !newName.trim()) {
    throw new Error('Category ID and name are required');
  }

  try {
    // Check if new name already exists
    const existingSnap = await getDocs(
      query(collection(firestore, CATEGORIES_COLLECTION), where('name', '==', newName.trim()))
    );

    if (!existingSnap.empty && existingSnap.docs[0].id !== categoryId) {
      throw new Error('Category name already exists');
    }

    await updateDoc(doc(firestore, CATEGORIES_COLLECTION, categoryId), {
      name: newName.trim(),
      updatedAt: serverTimestamp(),
    });

    return { id: categoryId, name: newName.trim() };
  } catch (error) {
    throw error;
  }
}

// Delete a category (for admin/coach/business advisor)
export async function deleteCategory(categoryId) {
  if (!categoryId) {
    throw new Error('Category ID is required');
  }

  try {
    // Get the category name first
    const categoryDoc = await getDoc(doc(firestore, CATEGORIES_COLLECTION, categoryId));
    if (!categoryDoc.exists()) {
      throw new Error('Category not found');
    }

    const categoryName = categoryDoc.data()?.name;

    // Check if any documents use this category
    const docsSnap = await getDocs(
      query(collection(firestore, DOCUMENTS_COLLECTION), where('category', '==', categoryName))
    );

    if (!docsSnap.empty) {
      throw new Error(
        'Cannot delete category: documents are still using it. Please reassign documents first.'
      );
    }

    await deleteDoc(doc(firestore, CATEGORIES_COLLECTION, categoryId));
    return { success: true };
  } catch (error) {
    throw error;
  }
}

export async function uploadDocument({
  file,
  ownerId,
  ownerEmail,
  category,
  uploadedByRole,
  notes = '',
  versionGroupKey = null,
  visibility = { type: 'private', roles: [], userIds: [], userEmails: [] },
  scope = 'client',
}) {
  if (!file) throw new Error('No file provided');
  if (!category) throw new Error('Category is required');
  const timestamp = Date.now();
  const safeName = file.name.replace(/[^a-zA-Z0-9_.-]/g, '_');
  const path = `documentBank/${ownerId}/${category}/${timestamp}_${safeName}`;
  const storageRef = ref(storage, path);
  const snapshot = await uploadBytes(storageRef, file);
  const downloadURL = await getDownloadURL(snapshot.ref);

  const docRef = await addDoc(collection(firestore, DOCUMENTS_COLLECTION), {
    ownerId,
    ownerEmail: normalizeEmailForStorage(ownerEmail),
    category,
    fileName: file.name,
    fileSize: file.size,
    fileType: file.type,
    storagePath: snapshot.ref.fullPath,
    url: downloadURL,
    uploadedAt: serverTimestamp(),
    uploadedByRole,
    notes,
    versionGroupKey: versionGroupKey || `${ownerId}:${category}:${safeName}`,
    visibility: normalizeDocumentVisibility(visibility),
    scope,
  });

  return { id: docRef.id, url: downloadURL };
}

// Helper function to validate and determine link type
function validateLink(url) {
  if (!url || typeof url !== 'string') {
    return { valid: false, type: null, error: 'Invalid URL' };
  }

  const trimmedUrl = url.trim();

  // Google Drive validation
  const googleDrivePatterns = [
    /^https?:\/\/(drive\.google\.com|docs\.google\.com)/i,
    /^https?:\/\/drive\.google\.com\/file\/d\/[a-zA-Z0-9_-]+/i,
    /^https?:\/\/drive\.google\.com\/open\?id=[a-zA-Z0-9_-]+/i,
    /^https?:\/\/docs\.google\.com\/(document|spreadsheets|presentation|forms)\/d\/[a-zA-Z0-9_-]+/i,
  ];

  // GitHub validation
  const githubPatterns = [
    /^https?:\/\/(github\.com|raw\.githubusercontent\.com)/i,
    /^https?:\/\/github\.com\/[a-zA-Z0-9_-]+\/[a-zA-Z0-9_.-]+\/(blob|tree|releases)\//i,
    /^https?:\/\/raw\.githubusercontent\.com\/[a-zA-Z0-9_-]+\/[a-zA-Z0-9_.-]+\//i,
  ];

  // Check Google Drive
  for (const pattern of googleDrivePatterns) {
    if (pattern.test(trimmedUrl)) {
      return { valid: true, type: 'google-drive', url: trimmedUrl };
    }
  }

  // Check GitHub
  for (const pattern of githubPatterns) {
    if (pattern.test(trimmedUrl)) {
      return { valid: true, type: 'github', url: trimmedUrl };
    }
  }

  // Check if it's a valid URL format
  try {
    new URL(trimmedUrl);
    return { valid: true, type: 'external-link', url: trimmedUrl };
  } catch {
    return { valid: false, type: null, error: 'Invalid URL format' };
  }
}

export async function uploadLink({
  link,
  linkName,
  ownerId,
  ownerEmail,
  category,
  uploadedByRole,
  notes = '',
  visibility = { type: 'private', roles: [], userIds: [], userEmails: [] },
  scope = 'client',
}) {
  if (!link) throw new Error('No link provided');
  if (!category) throw new Error('Category is required');

  const validation = validateLink(link);
  if (!validation.valid) {
    throw new Error(validation.error || 'Invalid link format');
  }

  // Extract name from URL if not provided
  let fileName = linkName?.trim();
  if (!fileName) {
    try {
      const url = new URL(validation.url);
      const pathParts = url.pathname.split('/').filter(Boolean);
      fileName = pathParts[pathParts.length - 1] || 'Untitled Document';
    } catch {
      fileName = 'Untitled Document';
    }
  }

  // Determine file type based on link type
  const linkType = validation.type;
  const fileType =
    linkType === 'google-drive'
      ? 'application/vnd.google-apps.document'
      : linkType === 'github'
        ? 'application/x-github-link'
        : 'application/x-external-link';

  const timestamp = Date.now();
  const versionGroupKey = `${ownerId}:${category}:${linkType}_${timestamp}`;

  const docRef = await addDoc(collection(firestore, DOCUMENTS_COLLECTION), {
    ownerId,
    ownerEmail: normalizeEmailForStorage(ownerEmail),
    category,
    fileName,
    fileSize: 0, // Links don't have file size
    fileType,
    storagePath: null, // Links don't have storage path
    url: validation.url,
    linkType, // Store the link type
    isLink: true, // Flag to indicate this is a link document
    uploadedAt: serverTimestamp(),
    uploadedByRole,
    notes,
    versionGroupKey,
    visibility: normalizeDocumentVisibility(visibility),
    scope,
  });

  return { id: docRef.id, url: validation.url };
}

export async function listDocuments({
  forOwnerId,
  category = null,
  fileType = null,
  search = '',
  pageSize = 20,
  last = null,
  includeStaffOnly = false,
  /** Omit or pass null to skip `scope` equality (legacy rows without `scope`). */
  scope = 'client',
}) {
  const constraints = [];
  if (forOwnerId) constraints.push(where('ownerId', '==', forOwnerId));
  if (category) constraints.push(where('category', '==', category));
  if (fileType) constraints.push(where('fileType', '==', fileType));
  if (scope != null && scope !== '') constraints.push(where('scope', '==', scope));

  const staffRoleLower = new Set([
    'admin',
    'admins',
    'superadmin',
    'superadmins',
    'moderator',
    'moderators',
    'coach',
    'coaches',
    'businesscoach',
    'platformengineer',
    'developer',
    's_admin',
  ]);

  try {
    let q = query(
      collection(firestore, DOCUMENTS_COLLECTION),
      ...constraints,
      orderBy('uploadedAt', 'desc'),
      limit(pageSize)
    );
    if (last) q = query(q, startAfter(last));

    const snap = await getDocs(q);
    let items = snap.docs.map(d => ({ id: d.id, ...d.data(), _snap: d }));

    const lowered = search?.toLowerCase?.() || '';
    let filtered = lowered
      ? items.filter(i => i.fileName?.toLowerCase?.().includes(lowered))
      : items;
    if (includeStaffOnly) {
      filtered = filtered.filter(i =>
        staffRoleLower.has(String(i.uploadedByRole || '').toLowerCase())
      );
    }

    return { items: filtered, lastSnap: snap.docs[snap.docs.length - 1] || null };
  } catch (err) {
    const message = String(err?.message || '');
    const needsIndex = message.includes('requires an index');
    if (!needsIndex) throw err;

    const q = query(collection(firestore, DOCUMENTS_COLLECTION), ...constraints);
    const snap = await getDocs(q);
    let items = snap.docs.map(d => ({ id: d.id, ...d.data(), _snap: d }));

    items.sort((a, b) => {
      const av = a.uploadedAt?.toMillis ? a.uploadedAt.toMillis() : 0;
      const bv = b.uploadedAt?.toMillis ? b.uploadedAt.toMillis() : 0;
      return bv - av;
    });

    const lowered = search?.toLowerCase?.() || '';
    let filtered = lowered
      ? items.filter(i => i.fileName?.toLowerCase?.().includes(lowered))
      : items;
    if (includeStaffOnly) {
      filtered = filtered.filter(i =>
        staffRoleLower.has(String(i.uploadedByRole || '').toLowerCase())
      );
    }
    filtered = filtered.slice(0, pageSize);

    return { items: filtered, lastSnap: null };
  }
}

export async function deleteDocument({ id, storagePath }) {
  if (!id) throw new Error('Missing id');
  try {
    if (storagePath) {
      const r = ref(storage, storagePath);
      await deleteObject(r);
    }
  } finally {
    await deleteDoc(doc(firestore, DOCUMENTS_COLLECTION, id));
  }
}

export async function updateDocumentMetadata(id, data) {
  const patch = { ...(data && typeof data === 'object' ? data : {}) };
  if (patch.visibility != null) {
    patch.visibility = normalizeDocumentVisibility(patch.visibility);
  }
  await updateDoc(doc(firestore, DOCUMENTS_COLLECTION, id), patch);
}

export async function reuploadDocument({
  id,
  oldStoragePath,
  newFile,
  ownerId,
  category,
  uploadedByRole,
}) {
  if (!newFile) throw new Error('No file provided');
  const timestamp = Date.now();
  const safeName = newFile.name.replace(/[^a-zA-Z0-9_.-]/g, '_');
  const path = `documentBank/${ownerId}/${category}/${timestamp}_${safeName}`;
  const storageRef = ref(storage, path);
  const snapshot = await uploadBytes(storageRef, newFile);
  const downloadURL = await getDownloadURL(snapshot.ref);

  await updateDoc(doc(firestore, DOCUMENTS_COLLECTION, id), {
    fileName: newFile.name,
    fileSize: newFile.size,
    fileType: newFile.type,
    storagePath: snapshot.ref.fullPath,
    url: downloadURL,
    uploadedAt: serverTimestamp(),
    uploadedByRole,
  });

  if (oldStoragePath) {
    try {
      await deleteObject(ref(storage, oldStoragePath));
    } catch (_) {}
  }

  return downloadURL;
}

export async function listAccessibleDocuments({
  viewer,
  ownerId = null,
  category = null,
  fileType = null,
  search = '',
  pageSize = 20,
}) {
  const lowerSearch = search?.toLowerCase?.() || '';

  const roles = Array.isArray(viewer?.roles) ? viewer.roles : [];

  // Must match firestore.rules isDocumentBankStaffFullAccess: admin, s_admin, moderator, coaches by Auth uid only
  const staffFullAccess = viewer?.documentBankStaffFullAccess === true || viewer?.isAdmin === true;

  const runStaffList = async () => {
    let allDocsQuery;
    if (ownerId) {
      allDocsQuery = query(
        collection(firestore, DOCUMENTS_COLLECTION),
        where('ownerId', '==', ownerId),
        orderBy('uploadedAt', 'desc')
      );
    } else {
      allDocsQuery = query(
        collection(firestore, DOCUMENTS_COLLECTION),
        orderBy('uploadedAt', 'desc')
      );
    }

    const snap = await getDocs(allDocsQuery);
    let items = snap.docs.map(d => ({ id: d.id, ...d.data() }));

    if (category) items = items.filter(i => i.category === category);
    if (fileType) items = items.filter(i => i.fileType === fileType);
    if (lowerSearch)
      items = items.filter(i => (i.fileName || '').toLowerCase().includes(lowerSearch));

    return { items: items.slice(0, pageSize), lastSnap: null };
  };

  if (staffFullAccess) {
    try {
      return await runStaffList();
    } catch (error) {
      const message = String(error?.message || '');
      const needsIndex = message.includes('requires an index');
      if (!needsIndex) {
        toast.error('Error fetching documents');
        return { items: [], lastSnap: null };
      }

      try {
        if (ownerId) {
          const q = query(
            collection(firestore, DOCUMENTS_COLLECTION),
            where('ownerId', '==', ownerId)
          );
          const snap = await getDocs(q);
          let items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
          items.sort((a, b) => {
            const av = a.uploadedAt?.toMillis ? a.uploadedAt.toMillis() : 0;
            const bv = b.uploadedAt?.toMillis ? b.uploadedAt.toMillis() : 0;
            return bv - av;
          });
          if (category) items = items.filter(i => i.category === category);
          if (fileType) items = items.filter(i => i.fileType === fileType);
          if (lowerSearch)
            items = items.filter(i => (i.fileName || '').toLowerCase().includes(lowerSearch));
          return { items: items.slice(0, pageSize), lastSnap: null };
        }
        const q = query(collection(firestore, DOCUMENTS_COLLECTION));
        const snap = await getDocs(q);
        let items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        items.sort((a, b) => {
          const av = a.uploadedAt?.toMillis ? a.uploadedAt.toMillis() : 0;
          const bv = b.uploadedAt?.toMillis ? b.uploadedAt.toMillis() : 0;
          return bv - av;
        });
        if (category) items = items.filter(i => i.category === category);
        if (fileType) items = items.filter(i => i.fileType === fileType);
        if (lowerSearch)
          items = items.filter(i => (i.fileName || '').toLowerCase().includes(lowerSearch));
        return { items: items.slice(0, pageSize), lastSnap: null };
      } catch (e2) {
        toast.error('Error fetching documents');
        return { items: [], lastSnap: null };
      }
    }
  }

  /** Firestore rejects the whole batch if any query is invalid; role-based queries can fail for mixed role sets. */
  const promises = [
    getDocs(
      query(collection(firestore, DOCUMENTS_COLLECTION), where('visibility.type', '==', 'public'))
    ),
  ];

  if (viewer?.uid) {
    promises.push(
      getDocs(
        query(collection(firestore, DOCUMENTS_COLLECTION), where('ownerId', '==', viewer.uid))
      )
    );
    promises.push(
      getDocs(
        query(
          collection(firestore, DOCUMENTS_COLLECTION),
          where('visibility.userIds', 'array-contains', viewer.uid)
        )
      )
    );
  }

  const emailRaw = viewer?.email ? String(viewer.email).trim() : '';
  const emailNorm = emailRaw ? emailRaw.toLowerCase() : '';
  if (emailNorm) {
    promises.push(
      getDocs(
        query(collection(firestore, DOCUMENTS_COLLECTION), where('ownerEmail', '==', emailNorm))
      )
    );
    if (emailRaw !== emailNorm) {
      promises.push(
        getDocs(
          query(collection(firestore, DOCUMENTS_COLLECTION), where('ownerEmail', '==', emailRaw))
        )
      );
    }
    promises.push(
      getDocs(
        query(
          collection(firestore, DOCUMENTS_COLLECTION),
          where('visibility.userEmails', 'array-contains', emailNorm)
        )
      )
    );
    if (emailRaw !== emailNorm) {
      promises.push(
        getDocs(
          query(
            collection(firestore, DOCUMENTS_COLLECTION),
            where('visibility.userEmails', 'array-contains', emailRaw)
          )
        )
      );
    }
  }

  const rolesForVisibilityQuery = roles.slice(0, 10);
  if (rolesForVisibilityQuery.length > 0) {
    promises.push(
      getDocs(
        query(
          collection(firestore, DOCUMENTS_COLLECTION),
          where('visibility.roles', 'array-contains-any', rolesForVisibilityQuery)
        )
      )
    );
  }

  const settled = await Promise.allSettled(promises);
  const byId = new Map();
  settled.forEach(res => {
    if (res.status !== 'fulfilled') return;
    const snap = res.value;
    snap.docs.forEach(d => {
      const data = { id: d.id, ...d.data() };
      byId.set(d.id, data);
    });
  });

  let items = Array.from(byId.values());

  if (category) items = items.filter(i => i.category === category);
  if (fileType) items = items.filter(i => i.fileType === fileType);
  if (lowerSearch)
    items = items.filter(i => (i.fileName || '').toLowerCase().includes(lowerSearch));

  items.sort((a, b) => {
    const av = a.uploadedAt?.toMillis
      ? a.uploadedAt.toMillis()
      : new Date(a.uploadedAt || 0).getTime();
    const bv = b.uploadedAt?.toMillis
      ? b.uploadedAt.toMillis()
      : new Date(b.uploadedAt || 0).getTime();
    return bv - av;
  });

  return { items: items.slice(0, pageSize), lastSnap: null };
}
